import type { Allergen } from '../../shared/menu';
import { findLanguage } from '../../shared/languages';
import type { LocalizedPhrase } from './dietaryPhrasebook';
import { useSettings } from './store';

export type StringKey =
  | 'app.tagline'
  | 'home.cta.scan'
  | 'home.cta.scanNew'
  | 'home.tab.you'
  | 'home.resume'
  | 'home.resume.untitled'
  | 'home.empty'
  | 'home.trust.translates'
  | 'home.trust.oneAllergen'
  | 'home.trust.manyAllergens'
  | 'splash.question'
  | 'scan.parsing.reading'
  | 'scan.parsing.translating'
  | 'scan.parsing.almost'
  | 'menu.toggleLanguage'
  | 'menu.showAnyway'
  | 'menu.empty'
  | 'dish.add'
  | 'dish.note'
  | 'dish.notePlaceholder'
  | 'dish.quickNoOnion'
  | 'dish.quickLessSpicy'
  | 'dish.quickOnSide'
  | 'dish.priceUnknown'
  | 'dish.dietary'
  | 'dish.dietaryHint'
  | 'dish.severityPreference'
  | 'dish.severityStrict'
  | 'dish.contradiction.title'
  | 'dish.contradiction.body'
  | 'dish.contradiction.override'
  | 'dish.contradiction.cancel'
  | 'dish.tag.vegan'
  | 'dish.tag.vegetarian'
  | 'dish.tag.gluten_free'
  | 'dish.tag.no_nuts'
  | 'dish.tag.no_dairy'
  | 'dish.tag.no_pork'
  | 'dish.tag.no_shellfish'
  | 'dish.tag.no_alcohol'
  | 'dish.tag.no_fish_sauce'
  | 'dish.tag.no_pork_lard'
  | 'dish.tag.no_cross_contamination'
  | 'cart.viewOrder'
  | 'cart.items'
  | 'order.modify'
  | 'order.placed'
  | 'order.dismiss'
  | 'confirm.title'
  | 'confirm.body'
  | 'confirm.cancel'
  | 'confirm.yes'
  | 'undo.placed'
  | 'undo.label'
  | 'saved.tabMenus'
  | 'saved.tabOrders'
  | 'orders.empty'
  | 'orders.itemCount'
  | 'orders.import.title'
  | 'orders.import.body'
  | 'orders.import.confirm'
  | 'orders.import.skip'
  | 'settings.title'
  | 'settings.language'
  | 'settings.dietary'
  | 'settings.allergens'
  | 'settings.about'
  | 'error.tryAgain';

const en: Record<StringKey, string> = {
  'app.tagline': 'Point. Tap. Eat.',
  'home.cta.scan': 'Take a photo of the menu',
  'home.cta.scanNew': 'Scan a new menu',
  'home.tab.you': 'You',
  'home.resume': 'Continue',
  'home.resume.untitled': 'Untitled menu',
  'home.empty': 'Snap a photo of any menu and we\u2019ll translate it.',
  'home.trust.translates': 'Translates to {lang}',
  'home.trust.oneAllergen': '1 allergen filter',
  'home.trust.manyAllergens': '{n} allergen filters',
  'splash.question': 'What language do you read?',
  'scan.parsing.reading': 'Reading the menu…',
  'scan.parsing.translating': 'Translating to your language…',
  'scan.parsing.almost': 'Almost ready…',
  'menu.toggleLanguage': 'Switch language',
  'menu.showAnyway': 'Show anyway',
  'menu.empty': 'No dishes in this category.',
  'dish.add': 'Add to order',
  'dish.note': 'Note for kitchen (optional)',
  'dish.notePlaceholder': 'e.g. no onions, less spicy…',
  'dish.quickNoOnion': 'No onions',
  'dish.quickLessSpicy': 'Less spicy',
  'dish.quickOnSide': 'On the side',
  'dish.priceUnknown': 'Ask',
  'dish.dietary': 'Dietary concerns',
  'dish.dietaryHint': 'Tap once for preference, again for strict / medical.',
  'dish.severityPreference': 'Preference',
  'dish.severityStrict': 'Strict / medical',
  'dish.contradiction.title': 'This dish may contain {allergen}',
  'dish.contradiction.body': 'You tagged this as {tag}. Choose another dish, remove the tag, or override.',
  'dish.contradiction.override': 'Override and add anyway',
  'dish.contradiction.cancel': 'Cancel',
  'dish.tag.vegan': 'Vegan',
  'dish.tag.vegetarian': 'Vegetarian',
  'dish.tag.gluten_free': 'Gluten-free',
  'dish.tag.no_nuts': 'No nuts',
  'dish.tag.no_dairy': 'No dairy',
  'dish.tag.no_pork': 'No pork',
  'dish.tag.no_shellfish': 'No shellfish',
  'dish.tag.no_alcohol': 'No alcohol',
  'dish.tag.no_fish_sauce': 'No fish sauce',
  'dish.tag.no_pork_lard': 'No pork lard',
  'dish.tag.no_cross_contamination': 'No cross-contamination',
  'cart.viewOrder': 'Modify order',
  'cart.items': 'items',
  'order.modify': 'Modify',
  'order.placed': 'Order placed',
  'order.dismiss': 'Dismiss',
  'confirm.title': 'Order placed?',
  'confirm.body': 'This saves to your history. You can view it later under Saved → History.',
  'confirm.cancel': 'Cancel',
  'confirm.yes': 'Yes, placed',
  'undo.placed': 'Order placed.',
  'undo.label': 'Undo',
  'saved.tabMenus': 'Menus',
  'saved.tabOrders': 'Orders',
  'orders.empty': 'No orders yet. Tap "Order placed" on a hand-off screen to save one.',
  'orders.itemCount': '{n} items',
  'orders.import.title': 'Import {n} local orders?',
  'orders.import.body': 'These were saved on this device while signed out. Import them to your account so they sync across devices.',
  'orders.import.confirm': 'Import',
  'orders.import.skip': 'Not now',
  'settings.title': 'Settings',
  'settings.language': 'Reading language',
  'settings.dietary': 'Dietary preferences',
  'settings.allergens': 'Hide dishes containing',
  'settings.about': 'About',
  'error.tryAgain': 'Try again',
};

const es: Partial<Record<StringKey, string>> = {
  'app.tagline': 'Apunta. Toca. Come.',
  'home.cta.scan': 'Hacer una foto del menú',
  'splash.question': '¿Qué idioma lees?',
  'scan.parsing.reading': 'Leyendo el menú…',
  'scan.parsing.translating': 'Traduciendo…',
  'scan.parsing.almost': 'Casi listo…',
  'cart.viewOrder': 'Ver pedido',
  'cart.items': 'platos',
  'dish.add': 'Añadir al pedido',
  'dish.note': 'Nota para la cocina (opcional)',
  'dish.notePlaceholder': 'ej. sin cebolla, menos picante…',
  'dish.quickNoOnion': 'Sin cebolla',
  'dish.quickLessSpicy': 'Menos picante',
  'dish.quickOnSide': 'Aparte',
  'dish.priceUnknown': 'Preguntar',
  'settings.title': 'Ajustes',
  'menu.showAnyway': 'Mostrar igualmente',
};

const ja: Partial<Record<StringKey, string>> = {
  'app.tagline': '指して、タップして、食べる。',
  'home.cta.scan': 'メニューを撮影する',
  'splash.question': 'どの言語を読みますか？',
  'cart.viewOrder': '注文を見る',
  'cart.items': '品',
  'dish.add': '注文に追加',
  'dish.priceUnknown': '要確認',
  'settings.title': '設定',
};

const TABLES: Record<string, Partial<Record<StringKey, string>>> = { en, es, ja, 'zh-TW': {} };

export function tFor(lang: string, key: StringKey): string {
  return TABLES[lang]?.[key] ?? en[key];
}

export function useT(): (key: StringKey) => string {
  const lang = useSettings((s) => s.targetLanguage);
  return (key) => tFor(lang, key);
}

const ORDER_GREETINGS_BY_SOURCE: Record<string, string> = {
  en: 'Hello, I would like to order:',
  es: 'Hola, me gustaría pedir:',
  fr: 'Bonjour, je voudrais commander :',
  it: 'Buongiorno, vorrei ordinare:',
  de: 'Hallo, ich möchte bestellen:',
  pt: 'Olá, gostaria de pedir:',
  ja: 'こんにちは、注文をお願いします：',
  zh: '您好，我想点菜：',
  'zh-TW': '您好，我想點菜：',
  ko: '안녕하세요, 주문하고 싶습니다:',
  th: 'สวัสดีครับ/ค่ะ ขอสั่งอาหารครับ/ค่ะ:',
  vi: 'Xin chào, tôi muốn gọi món:',
  ar: 'مرحبًا، أود أن أطلب:',
  he: 'שלום, אני רוצה להזמין:',
  tr: 'Merhaba, sipariş vermek istiyorum:',
  el: 'Γεια σας, θα ήθελα να παραγγείλω:',
  ru: 'Здравствуйте, я хочу заказать:',
  pl: 'Dzień dobry, chciałbym zamówić:',
  nl: 'Hallo, ik wil graag bestellen:',
  hi: 'नमस्ते, मैं ऑर्डर करना चाहूंगा:',
  id: 'Halo, saya mau pesan:',
  cs: 'Dobrý den, chtěl bych si objednat:',
  uk: 'Доброго дня, я хочу замовити:',
};

const ORDER_THANKS_BY_SOURCE: Record<string, string> = {
  en: 'Thank you.',
  es: 'Gracias.',
  fr: 'Merci.',
  it: 'Grazie.',
  de: 'Danke.',
  pt: 'Obrigado.',
  ja: 'ありがとうございます。',
  zh: '谢谢。',
  'zh-TW': '謝謝。',
  ko: '감사합니다.',
  th: 'ขอบคุณครับ/ค่ะ',
  vi: 'Cảm ơn.',
  ar: 'شكرًا.',
  he: 'תודה.',
  tr: 'Teşekkürler.',
  el: 'Ευχαριστώ.',
  ru: 'Спасибо.',
  pl: 'Dziękuję.',
  nl: 'Dank u.',
  hi: 'धन्यवाद।',
  id: 'Terima kasih.',
  cs: 'Děkuji.',
  uk: 'Дякую.',
};

const ALLERGY_BANNER_BY_SOURCE: Record<string, string> = {
  en: 'ALLERGY',
  es: 'ALERGIA',
  fr: 'ALLERGIE',
  it: 'ALLERGIA',
  de: 'ALLERGIE',
  pt: 'ALERGIA',
  ja: 'アレルギー',
  zh: '过敏',
  'zh-TW': '過敏',
  ko: '알레르기',
  th: 'แพ้อาหาร',
  vi: 'DỊ ỨNG',
  ar: 'حساسية',
  he: 'אלרגיה',
  tr: 'ALERJİ',
  el: 'ΑΛΛΕΡΓΙΑ',
  ru: 'АЛЛЕРГИЯ',
  pl: 'ALERGIA',
  nl: 'ALLERGIE',
  hi: 'एलर्जी',
  id: 'ALERGI',
  cs: 'ALERGIE',
  uk: 'АЛЕРГІЯ',
};

// Safety disclaimer shown on the hand-off screen — reminds the waiter to
// confirm with the kitchen rather than nodding without checking.
const CONFIRM_WITH_SERVER_BY_SOURCE: Record<string, string> = {
  en: 'Please confirm with the kitchen.',
  es: 'Por favor confirme con la cocina.',
  fr: 'Veuillez confirmer avec la cuisine.',
  it: 'Si prega di confermare con la cucina.',
  de: 'Bitte mit der Küche bestätigen.',
  pt: 'Por favor confirme com a cozinha.',
  ja: '厨房にご確認ください。',
  zh: '请向厨房确认。',
  'zh-TW': '請向廚房確認。',
  ko: '주방에 확인해 주세요.',
  th: 'กรุณาตรวจสอบกับห้องครัว',
  vi: 'Vui lòng xác nhận với bếp.',
};

export function orderGreetingFor(sourceLanguage: string): string {
  return ORDER_GREETINGS_BY_SOURCE[sourceLanguage] ?? 'Hello, I would like to order:';
}

export function orderThanksFor(sourceLanguage: string): string {
  return ORDER_THANKS_BY_SOURCE[sourceLanguage] ?? 'Thank you.';
}

export function allergyBannerFor(sourceLanguage: string): string {
  return ALLERGY_BANNER_BY_SOURCE[sourceLanguage] ?? 'ALLERGY';
}

export function confirmWithServerFor(sourceLanguage: string): LocalizedPhrase {
  const key = findLanguage(sourceLanguage)?.code ?? sourceLanguage;
  const native = CONFIRM_WITH_SERVER_BY_SOURCE[key];
  if (native) return { text: native, lang: key, translated: true };
  return { text: CONFIRM_WITH_SERVER_BY_SOURCE.en!, lang: 'en', translated: false };
}

// Hand-off-screen warning shown when the user explicitly overrode a
// contradiction between a strict dietary tag and a confirmed allergen on
// a dish. The traveler sees this; the waiter (ideally) reads the source
// language. {allergens} is replaced with the comma-joined list of
// override allergens at render time.
//
// Only English is shipped today — non-English entries pending native-speaker
// QA. The fallback chain renders English with a visible warning so the
// signal is never silently dropped.
const OVERRIDE_WARN_BY_SOURCE: Record<string, string> = {
  en: 'KITCHEN: confirm this dish is free of {allergens}. Cross-contamination matters.',
};

export function overrideWarnFor(sourceLanguage: string, allergens: Allergen[]): LocalizedPhrase {
  const list = allergens.join(', ');
  const key = findLanguage(sourceLanguage)?.code ?? sourceLanguage;
  const native = OVERRIDE_WARN_BY_SOURCE[key];
  if (native) {
    return { text: native.replace('{allergens}', list), lang: key, translated: true };
  }
  return {
    text: OVERRIDE_WARN_BY_SOURCE.en!.replace('{allergens}', list),
    lang: 'en',
    translated: false,
  };
}
